import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * The turn per variant (ui-ux.md §6 "Perspective cover"). Tailwind's `hover:`
 * is gated to fine hovering pointers (tailwind.config.ts), so touch stays at
 * rest; `motion-safe:` keeps reduced motion at the rest pose with no change.
 */
const POSE = {
  /** Rails: flat at rest; hover or keyboard focus inside turns the case. */
  hover:
    'motion-safe:group-hover/case:[transform:rotateY(-20deg)_scale(1.066)_translateX(-4px)] motion-safe:group-has-[:focus-visible]/case:[transform:rotateY(-20deg)_scale(1.066)_translateX(-4px)]',
  /** The hero: rests turned (spine + edge showing), eases toward the viewer on hover. */
  rest: '[transform:rotateY(-18deg)] motion-safe:group-hover/case:[transform:rotateY(-8deg)_scale(1.03)]',
  /**
   * Grids: a gentle lift on hover/focus, no scale. The perspective lives in
   * the transform itself, so at rest there is no transform, no 3D context
   * and no layer — a wall of 100+ covers costs nothing until one is hovered.
   */
  lift: 'motion-safe:group-hover/case:[transform:perspective(900px)_rotateY(-10deg)] motion-safe:group-has-[:focus-visible]/case:[transform:perspective(900px)_rotateY(-10deg)]',
} as const;

export interface PerspectiveCoverProps {
  /**
   * `hover` for rails (turns on hover/focus), `rest` for the hero (rests
   * turned), `lift` for grids (a flat 2D cover until hover; no edge face).
   */
  variant: keyof typeof POSE;
  /**
   * The front face: a `Poster` (with `layout={false} lift={false}`; under
   * `lift` the poster may keep its `layout` morph), usually inside its link,
   * plus any overlay that belongs on the cover (a quick mark). The front is
   * `relative`, so `absolute` overlays sit on the poster.
   */
  children: ReactNode;
  /** Width etc. on the outer box — the case fills it. */
  className?: string;
}

/**
 * A poster as a physical case (ui-ux.md §6 "Perspective cover") — the one
 * sanctioned 3D object. A CSS-3D box: the poster is the front face, with the
 * hinge highlight on its spine (`.case-spine`), and a right-hand edge face
 * (`.case-edge`, the case's thickness, 7% of the width) folded back behind
 * it, which only shows once the case turns. The front sits at z = 0, so at
 * rest a `hover` case is the plain, crisp poster. `lift` is the light grid
 * pose: no 3D box and no edge, the spine fades in with the turn.
 */
export function PerspectiveCover({ variant, children, className }: PerspectiveCoverProps) {
  const lift = variant === 'lift';
  return (
    <div className={cn('group/case', !lift && '[container-type:inline-size] [perspective:900px]', className)}>
      <div
        className={cn(
          'relative transition-transform duration-slow ease-out motion-reduce:transition-none',
          !lift && '[transform-style:preserve-3d]',
          POSE[variant],
        )}
      >
        <div className={cn('relative isolate rounded-sm', !lift && 'shadow-poster')}>
          {children}
          <div
            aria-hidden
            className={cn(
              'case-spine pointer-events-none absolute inset-y-0 left-0 z-[1] w-[8%] rounded-l-sm',
              lift
                ? 'opacity-0 transition-opacity duration-slow motion-safe:group-hover/case:opacity-40 motion-safe:group-has-[:focus-visible]/case:opacity-40'
                : 'opacity-40',
            )}
          />
        </div>
        {lift ? null : (
          <div
            aria-hidden
            className="case-edge pointer-events-none absolute inset-y-0 right-0 w-[7cqw] origin-right [transform:rotateY(-90deg)]"
          />
        )}
      </div>
    </div>
  );
}
